import { Component, Vue } from 'vue-facing-decorator';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Check, Close, Search, Clock, Ticket, WarningFilled, Refresh, ChatLineRound, Unlock, WarnTriangleFilled } from '@element-plus/icons-vue';
import Status from "@/models/EnumStatus";
import Header from '@/components/Header/Header.vue';
import Navigation from '@/components/Navigation/Navigation.vue';
import NavigationIndex from "@/models/EnumNavigationIndex";
import Breadcrumbs from '@/components/Breadcrumbs/Breadcrumbs.vue';
import AuthService from '@/services/AuthService';
import ApiService from '@/services/ApiService';
import WebSocketService from '@/services/WebSocketService';

@Component(
{
    components:
    {
        Header,
        Breadcrumbs,
        Navigation,
        Check,
        Close,
		Search,
        Clock,
        Ticket,
        WarningFilled,
        Refresh,
        ChatLineRound,
        Unlock,
        WarnTriangleFilled
    },
    enums:
    {
        NavigationIndex,
        Status
    }
})

export default class Questionnaire extends Vue 
{
    private loading : boolean = true;
    private generating : boolean = false;
    private loadingQuestions : boolean = true;
    private loadingOptions : boolean = true;
    private loadingGroups : boolean = true;
    private loadingParticipants : boolean = true;
    private showQuestions : boolean = false;
    private showQuestionPreview : boolean = false;
    private showCorrectAnswer : boolean = false;
    private allParticipantsReady : boolean | undefined = false;
    private allQuestionsApproved : boolean | undefined = false;
    private hasPermission: boolean[] = [] as boolean[];
    private testId : number = 0;
    private testName : string = '';
    private testDate : Date = new Date();
    private targetgroupNames : string = '';
    private testModel : TestModel | undefined = {} as TestModel;
    private questionnaireModelList : QuestionnaireView[] | undefined;
    private optionModelList : OptionsForQuestionModel[] | undefined;
    private targetgroupModelList : TestTargetGroupsModel[] | undefined;
    private groupModelList : OptionsGroupModel[] | undefined;
    private participantList : ParticipantModel[] | undefined = [];
    private step : number = 1;
    private substep : number  = 1;
    private questionId : string = "";
    private questionName : string = "";
    private questionnaireIdToReplace : number = 0;
    private testComplexity : string [] = ['Single Choice', 'Multiple choice'];
    private letters : string[] = ['#','A', 'B', 'C', 'D', 'E'];
    private answers : string[] = ['','','','',''];
    private correctIndex : boolean[] = [false, false, false, false, false];
    private loginAddress : string = "https://ddg.health/4579a0ab0122e59e";
    private windowWidth : number = window.innerWidth;
    private windowHeight : number = window.innerHeight;

    public async mounted()
    {
        // get test url from config
        this.loginAddress = process.env.VUE_APP_TEST_URL;

        // get permissions
        await AuthService.getPermissions();
        this.hasPermission['TEST_SELECT'] = AuthService.hasPermission('TEST_SELECT');
        this.hasPermission['TEST_INSERT'] = AuthService.hasPermission('TEST_INSERT');
        this.hasPermission['TEST_UPDATE'] = AuthService.hasPermission('TEST_UPDATE');
        this.hasPermission['TEST_DELETE'] = AuthService.hasPermission('TEST_DELETE');
        this.hasPermission['TEST_QUESTION'] = AuthService.hasPermission('TEST_QUESTION');
        
        if (this.hasPermission['TEST_UPDATE'])
        {
            // get test id from get request
            this.testId = this.$route.params.testid as string;
            
            // get step from params
            this.step = parseInt(this.$route.params.step) as number;

            // get substep from params
            this.substep = parseInt(this.$route.params.substep) as number;

            // load test from db
            this.testModel = await ApiService.getTest(this.testId);
            
            if (!this.testModel) 
            {
                this.$router.push('/test');
                return;
            }
            else
            {
                if (this.testModel.statusid == Status.TEST_START && this.step < 2)
                {
                    // remove websocket listener
                    WebSocketService.removeListener(this.onTestStateUpdate);

                    // go to question preview
                    this.$router.push(`/questionnaire/${this.testId}/1/3`);
                }

                this.targetgroupModelList = await ApiService.getTestTargetgroups(this.testId);
                
                this.targetgroupNames = "";
                for (let t : TestTargetGroupsModel of this.targetgroupModelList)
                {
                    if (this.targetgroupNames.length > 0) this.targetgroupNames += ", ";

                    this.targetgroupNames += t.name;
                }

                await this.loadQuestionnaire();

                this.testName = this.testModel.name;
                this.testDate = this.testModel.testdate;
            }

            await this.processStep();
        }

        window.addEventListener('resize', this.onWindowResize);

        this.loading = false;
    }
    
	private onWindowResize(e : any)
    {
        this.windowWidth = window.innerWidth;
        this.windowHeight = window.innerHeight;
    }

    private beforeUnmount()
    {
        // remove resize listener
        window.removeEventListener('resize', this.onWindowResize);

        // remove websocket listener
        WebSocketService.removeListener(this.onTestStateUpdate);
    }

    private async processStep()
    {
        if (this.step == 1 && this.substep == 1 && this.questionnaireModelList)
        {
            this.$router.push(`/questionnaire/${this.testId}/1/2`);
        }
        
        if (this.step == 1 && this.substep == 3)
        {
            await this.loadQuestionnaire();

            if (!this.questionnaireModelList || this.questionnaireModelList.length <= 0)
            {
                this.$router.push(`/questionnaire/${this.testId}/1/1`);
                return;
            }
        }

        if (this.step == 2 && this.substep == 1)
        {
            await this.loadParticipants();
            WebSocketService.openConnection();
            WebSocketService.addListener(this.onTestStateUpdate);
        }
    }
    
    private async checkParticipantStates()
    {
        if (!this.participantList || this.participantList.length <= 0) return;
        
        let ready : boolean = true;
        
        let missing : number = 0;
        for (let p : ParticipantModel of this.participantList)
        {
            ready = ready && ((!p.islocked && p.datelogin > 0) || p.isignored) as boolean;
            if (p.isignored) missing += 1;
        }

        this.allParticipantsReady = ready && missing < this.participantList.length;
    }

    private async loadQuestionnaire()
    {
        let approveCount : number = 0;
        this.loadingQuestions = true;
        this.questionnaireModelList = await ApiService.getQuestionnaireForTest(this.testModel.testid.toString());
        
        if (this.questionnaireModelList)
        {
            for (let q : QuestionnaireModel of this.questionnaireModelList)
            {
                if (q.isapproved) approveCount++;
            }
        }
        
        if (this.questionnaireModelList)
        {
            this.allQuestionsApproved = approveCount == this.questionnaireModelList.length;
        }

        this.loadingQuestions = false;
    }

    private async loadParticipants()
    {
        this.loadingParticipants = true;
        this.participantList = await ApiService.getParticipantsForTest(this.testModel.testid.toString());
        this.loadingParticipants = false;

        await this.checkParticipantStates();
    }

    private async loadQuestionOptions(questionid : string)
    {
        this.loadingOptions = true;
        this.optionModelList = await ApiService.getOptionsForQuestion(this.testModel.testid.toString(), questionid);
        this.loadingOptions = false;
    }

    private async loadOptionGroups(questionid : string)
    {
        this.loadingGroups = true;
        this.groupModelList = await ApiService.getOptionGroups(this.testModel.testid.toString(), questionid);
        this.loadingGroups = false;
    }

    private async generateQuestionnaire()
    {
        this.generating = true;
        if (this.testModel && this.step == 1 && this.substep == 1)
        {
            await ApiService.generateQuestionnaire(this.testModel.testid.toString());
            this.substep = 3;
            await this.processStep();
        }
        this.generating = false;
    }

    public async updateParticipant(participant : ParticipantModel)
    {
        if (participant)
        {
            await ApiService.updateParticipant(participant);
        }

        this.checkParticipantStates();
    }

    private allApproved()
    {
        return this.allQuestionsApproved;
    }

    private async getCorrectCountByQuestion(questionnaire : QuestionnaireModel)
    {
        let options = await ApiService.getOptionsForQuestion(this.testModel.testid.toString(), questionnaire.questionid);
        let numCorrect : number = 0;
        
        if (options && options.length > 0)
        {
            for (let o of options)
            {
                if (o.iscorrect)
                {
                    numCorrect++;
                }
            }
        }

        return numCorrect;
    }

    public async onGotoQuestionsClick()
    {
        this.$router.push(`/questionnaire/${this.testId}/1/3`);
    }

    public async onGotoParticipantsClick()
    {
        this.$router.push(`/questionnaire/${this.testId}/2/1`);
    }

    public onGenerateQuestionsClick()
    {
        this.generateQuestionnaire();
    }

    public async onQuestionPreviewClick(questionid : number, question : string)
    {
        this.questionId = questionid.toString();
        this.questionName = question;
        this.showQuestionPreview = true;

        await this.loadQuestionOptions(questionid.toString());
    }
    
    public async onBackToTestClick()
    {
        this.$router.push(`/test/${this.testModel.testid}`);
    }

    public async onUnlockParticipantClick(participant : ParticipantModel)
    {
        let ok = await ApiService.unlockParticipant(participant.participantid.toString());
        
        if (ok)
        {
            participant.islocked = !participant.islocked;
        }
    }

    public async onStartTestClick()
    {
        if (this.testModel && this.testModel.testid > 0)
        {
            this.testModel = await ApiService.startTest(this.testModel);

            if (this.testModel.statusid == Status.TEST_START)
            {
                this.$router.push(`/questionnaire/${this.testId}/audit/3/1`);
            }
        }
    }

    public async onWatchTestClick()
    {
        if (this.testModel && this.testModel.testid > 0)
        {
            this.$router.push(`/questionnaire/${this.testId}/audit/3/1`);
        }
    }

    public async onTestStateUpdate(message : string)
    {
        await this.loadParticipants();
    }

    public onExtraTimeChange(participant : ParticipantModel)
    {
        this.updateParticipant(participant);
    }

    public onIgnoreChange(participant : ParticipantModel)
    {
        this.updateParticipant(participant);
    }
}
